import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { LogOut, Users, FileText, Plus, MoreVertical, Pencil, Trash2, RefreshCw } from "lucide-react";

export default function AdminDashboard() {
  const { user, logout, api } = useAuth();
  const navigate = useNavigate();
  const [leads, setLeads] = useState([]);
  const [posts, setPosts] = useState([]);
  const [blogDialog, setBlogDialog] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({ title: "", excerpt: "", content: "", published: true });

  const loadLeads = useCallback(async () => {
    try {
      const { data } = await api.get("/leads");
      setLeads(data);
    } catch {
      toast.error("Erro ao carregar leads");
    }
  }, [api]);

  const loadPosts = useCallback(async () => {
    try {
      const { data } = await api.get("/blog?published_only=false");
      setPosts(data);
    } catch {
      toast.error("Erro ao carregar posts");
    }
  }, [api]);

  useEffect(() => {
    loadLeads();
    loadPosts();
  }, [loadLeads, loadPosts]);

  const updateLeadStatus = async (leadId, status) => {
    try {
      await api.patch(`/leads/${leadId}`, { status });
      loadLeads();
      toast.success("Status atualizado");
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const deleteLead = async (leadId) => {
    try {
      await api.delete(`/leads/${leadId}`);
      loadLeads();
      toast.success("Lead removido");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const openBlogDialog = (post = null) => {
    if (post) {
      setEditingPost(post);
      setBlogForm({ title: post.title, excerpt: post.excerpt, content: post.content, published: post.published });
    } else {
      setEditingPost(null);
      setBlogForm({ title: "", excerpt: "", content: "", published: true });
    }
    setBlogDialog(true);
  };

  const saveBlogPost = async () => {
    try {
      if (editingPost) {
        await api.put(`/blog/${editingPost.id}`, blogForm);
        toast.success("Post atualizado");
      } else {
        await api.post("/blog", blogForm);
        toast.success("Post criado");
      }
      setBlogDialog(false);
      loadPosts();
    } catch {
      toast.error("Erro ao salvar post");
    }
  };

  const deletePost = async (postId) => {
    try {
      await api.delete(`/blog/${postId}`);
      loadPosts();
      toast.success("Post removido");
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/admin");
  };

  const statusMap = {
    novo: { label: "Novo", cls: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30" },
    "em contato": { label: "Em contato", cls: "bg-yellow-500/20 text-yellow-300 border-yellow-500/30" },
    finalizado: { label: "Finalizado", cls: "bg-green-500/20 text-green-300 border-green-500/30" },
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header data-testid="admin-header" className="border-b border-border/30 px-6 md:px-8 py-4 flex justify-between items-center">
        <div className="flex items-center gap-3">
          <img src="https://customer-assets.emergentagent.com/job_importacao-expert/artifacts/gz8vakfs_viamundi_logo_sem_fundo_cores_originais.png" alt="Viamundi" className="h-10 w-auto object-contain" />
          <span className="text-xs text-muted-foreground tracking-wide">Painel Admin</span>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-sm text-muted-foreground hidden sm:block">{user?.email}</span>
          <Button data-testid="logout-btn" variant="ghost" size="icon" onClick={handleLogout} title="Sair">
            <LogOut className="h-4 w-4" />
          </Button>
        </div>
      </header>

      <div className="p-6 md:p-8 max-w-7xl mx-auto">
        <Tabs defaultValue="leads">
          <TabsList data-testid="admin-tabs" className="bg-card border border-border/30">
            <TabsTrigger data-testid="tab-leads" value="leads" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <Users className="h-4 w-4 mr-2" /> Leads ({leads.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-blog" value="blog" className="data-[state=active]:bg-primary/10 data-[state=active]:text-primary">
              <FileText className="h-4 w-4 mr-2" /> Blog ({posts.length})
            </TabsTrigger>
          </TabsList>

          {/* LEADS TAB */}
          <TabsContent value="leads" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-lg font-bold">Leads recebidos</h2>
              <Button data-testid="refresh-leads" variant="ghost" size="icon" onClick={loadLeads}>
                <RefreshCw className="h-4 w-4" />
              </Button>
            </div>
            <div className="border border-border/30 rounded-2xl overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/30 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider">Nome</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Email</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden md:table-cell">Telefone</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden lg:table-cell">Mensagem</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden sm:table-cell">Data</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {leads.map((lead) => (
                    <TableRow key={lead.id} className="border-border/20">
                      <TableCell data-testid={`lead-nome-${lead.id}`} className="font-medium text-sm">{lead.nome}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{lead.email}</TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden md:table-cell">{lead.telefone || "-"}</TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden lg:table-cell max-w-[200px] truncate">{lead.mensagem}</TableCell>
                      <TableCell>
                        <Select value={lead.status} onValueChange={(v) => updateLeadStatus(lead.id, v)}>
                          <SelectTrigger data-testid={`lead-status-${lead.id}`} className="w-[130px] h-8 bg-transparent border-none p-0">
                            <Badge className={`${statusMap[lead.status]?.cls || "bg-secondary text-secondary-foreground"} border text-xs`}>
                              {statusMap[lead.status]?.label || lead.status}
                            </Badge>
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="novo">Novo</SelectItem>
                            <SelectItem value="em contato">Em contato</SelectItem>
                            <SelectItem value="finalizado">Finalizado</SelectItem>
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">
                        {new Date(lead.created_at).toLocaleDateString("pt-BR")}
                      </TableCell>
                      <TableCell>
                        <Button
                          data-testid={`delete-lead-${lead.id}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteLead(lead.id)}
                          className="h-8 w-8 text-muted-foreground hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {leads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-12">
                        Nenhum lead recebido ainda
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </TabsContent>

          {/* BLOG TAB */}
          <TabsContent value="blog" className="mt-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="font-heading text-lg font-bold">Posts do blog</h2>
              <Button data-testid="new-post-btn" onClick={() => openBlogDialog()} className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Plus className="h-4 w-4 mr-2" /> Novo Post
              </Button>
            </div>
            <div className="border border-border/30 rounded-2xl overflow-hidden bg-card">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/30 hover:bg-transparent">
                    <TableHead className="text-xs uppercase tracking-wider">Titulo</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden md:table-cell">Excerto</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider">Status</TableHead>
                    <TableHead className="text-xs uppercase tracking-wider hidden sm:table-cell">Data</TableHead>
                    <TableHead className="w-10"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => (
                    <TableRow key={post.id} className="border-border/20">
                      <TableCell data-testid={`post-title-${post.id}`} className="font-medium text-sm">{post.title}</TableCell>
                      <TableCell className="text-sm text-muted-foreground hidden md:table-cell max-w-[300px] truncate">{post.excerpt}</TableCell>
                      <TableCell>
                        <Badge className={`${post.published ? "bg-green-500/20 text-green-300 border-green-500/30" : "bg-secondary text-secondary-foreground border-border"} border text-xs`}>
                          {post.published ? "Publicado" : "Rascunho"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground hidden sm:table-cell">
                        {new Date(post.created_at).toLocaleDateString("pt-BR")}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button data-testid={`post-actions-${post.id}`} variant="ghost" size="icon" className="h-8 w-8">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem data-testid={`edit-post-${post.id}`} onClick={() => openBlogDialog(post)}>
                              <Pencil className="h-4 w-4 mr-2" /> Editar
                            </DropdownMenuItem>
                            <DropdownMenuItem data-testid={`delete-post-${post.id}`} onClick={() => deletePost(post.id)} className="text-destructive focus:text-destructive">
                              <Trash2 className="h-4 w-4 mr-2" /> Excluir
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                  {posts.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center text-muted-foreground py-12">
                        Nenhum post criado ainda
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>

            {/* Blog Dialog */}
            <Dialog open={blogDialog} onOpenChange={setBlogDialog}>
              <DialogContent data-testid="blog-dialog" className="bg-card border-border/50 max-w-lg">
                <DialogHeader>
                  <DialogTitle className="font-heading">{editingPost ? "Editar Post" : "Novo Post"}</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Titulo</Label>
                    <Input
                      data-testid="blog-form-title"
                      value={blogForm.title}
                      onChange={(e) => setBlogForm((f) => ({ ...f, title: e.target.value }))}
                      className="mt-2 bg-background/50 border-border/50"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Excerto</Label>
                    <Input
                      data-testid="blog-form-excerpt"
                      value={blogForm.excerpt}
                      onChange={(e) => setBlogForm((f) => ({ ...f, excerpt: e.target.value }))}
                      className="mt-2 bg-background/50 border-border/50"
                    />
                  </div>
                  <div>
                    <Label className="text-xs uppercase tracking-wider text-muted-foreground">Conteudo</Label>
                    <Textarea
                      data-testid="blog-form-content"
                      value={blogForm.content}
                      onChange={(e) => setBlogForm((f) => ({ ...f, content: e.target.value }))}
                      rows={6}
                      className="mt-2 bg-background/50 border-border/50 resize-none"
                    />
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch
                      data-testid="blog-form-published"
                      checked={blogForm.published}
                      onCheckedChange={(v) => setBlogForm((f) => ({ ...f, published: v }))}
                    />
                    <Label className="text-sm">Publicado</Label>
                  </div>
                  <Button
                    data-testid="blog-form-save"
                    onClick={saveBlogPost}
                    className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    {editingPost ? "Salvar alteracoes" : "Criar post"}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
